import React, { useMemo, useState } from 'react';

import { isUnseenValue, normalizeRow, toBool, validateRow } from './utils';
import { CheckboxRow, FormTextField, ModalShell } from '../../components';

import './styles.scoped.css';

/**
 * CP-857: add / edit one code table row.
 *
 * Save stays enabled and validates on press (house rule) — errors render inline under the
 * field that produced them. Server-side rejections (duplicate key, and the proc's advisory
 * notes) arrive as one form-level message, because those are not attributable to a single
 * field the user can see.
 *
 * The key field is editable on an edit: these tables have no surrogate key, so re-keying is
 * how a misspelled status or worklist gets corrected, and the API duplicate-checks the
 * destination before allowing it.
 */
const CodeTableForm = ({ config, row, isEdit, known, onSave, onDelete, onCancel }) => {
  const [form, setForm] = useState(() => normalizeRow(config, row || {}));
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const busy = saving || deleting;

  // Not blocking -- the proc accepts an unseen value on purpose, because one can be
  // classified or mapped ahead of its first application. It is a nudge to catch the typo.
  const keyIsUnseen = useMemo(() => isUnseenValue(form[config.keyField], known), [form, config.keyField, known]);

  const handleChange = (key, value) => {
    setForm(current => ({ ...current, [key]: value }));
    // clear this field's error as soon as it is edited, and drop any stale server message
    setErrors(current => (current[key] ? { ...current, [key]: undefined } : current));
    setFormError('');
  };

  const handleSave = () => {
    const normalized = normalizeRow(config, form);
    const nextErrors = validateRow(config, normalized);

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSaving(true);
    setFormError('');
    // Clearing the in-flight flag belongs in catch, not finally: on success the parent
    // closes this dialog, so a finally would set state on an unmounted component.
    onSave(normalized).catch(e => {
      setFormError(e.message);
      setSaving(false);
    });
  };

  const handleDelete = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setDeleting(true);
    setFormError('');
    onDelete().catch(e => {
      setFormError(e.message);
      setConfirmingDelete(false);
      setDeleting(false);
    });
  };

  // Save / (Delete) / Cancel. `disabled` here is the in-flight reentrancy guard only --
  // Save is never disabled to gate missing input, it validates on press.
  const buttons = [{ text: saving ? 'Saving…' : 'Save', onPress: handleSave, disabled: busy }];
  if (isEdit) {
    buttons.push({
      text: confirmingDelete ? 'Confirm Delete' : 'Delete',
      variant: 'warning',
      onPress: handleDelete,
      disabled: busy,
    });
  }
  buttons.push({ text: 'Cancel', variant: 'outline', onPress: onCancel, disabled: busy });

  return (
    <ModalShell
      title={`${isEdit ? 'Edit' : 'Add'} ${config.rowNoun}`}
      subtitle={isEdit ? String(row?.[config.keyField] ?? '') : `${config.keyLabel} identifies the row.`}
      buttons={buttons}>
      <div className="form-grid">
        {config.fields.map(field =>
          field.type === 'boolean' ? (
            <CheckboxRow
              key={field.key}
              label={field.label}
              checked={toBool(form[field.key])}
              disabled={busy}
              onChange={event => handleChange(field.key, event.target.checked)}
            />
          ) : (
            <FormTextField
              key={field.key}
              label={field.label}
              type={field.type === 'number' ? 'number' : 'text'}
              required={field.required}
              disabled={busy}
              error={!!errors[field.key]}
              errorMessage={errors[field.key]}
              value={form[field.key]}
              onChange={event => handleChange(field.key, event.target.value)}
            />
          ),
        )}
      </div>

      {keyIsUnseen && <p className="form-note">{config.unseenWarning} Check the spelling.</p>}

      {confirmingDelete && !formError && <p className="form-warning">{config.deleteWarning}</p>}

      {!!formError && <p className="form-error">{formError}</p>}
    </ModalShell>
  );
};

export default CodeTableForm;
